import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function WestBeachPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'West Beach Painters Cape Town | Beachfront Painting Services | Blouberg Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services in West Beach, Cape Town. Specialist beachfront and coastal painting for Blouberg properties. Expert marine-grade coatings and wind-resistant systems.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'West Beach painters, Blouberg painting Cape Town, beachfront property painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/west-beach`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'West Beach Painters Cape Town',
        description: 'Professional painting services in West Beach, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/west-beach`,
        areaServed: {
          '@type': 'Place',
          name: 'West Beach, Blouberg, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -33.8089,
            longitude: 18.4589,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('West Beach Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Beachfront Property Painting',
      description:
        'Specialized painting services for West Beach\'s oceanfront properties with advanced marine-grade coatings designed to withstand direct Atlantic exposure. Our protective systems defend against salt spray, strong winds, and intense UV radiation while maintaining stunning aesthetics that complement the beach lifestyle and spectacular Table Mountain views.',
      features: [
        'Marine-grade protection',
        'Salt spray resistance',
        'Wind-resistant systems',
        'UV protection',
        'Beachfront expertise',
        'Long-term durability'
      ],
    },
    {
      icon: 'ri-building-2-line',
      title: 'Coastal Residential Painting',
      description:
        'Comprehensive painting services for West Beach homes using premium coastal-grade materials. Our approach ensures your property maintains its pristine appearance despite the challenging Atlantic coastal environment. We understand the unique demands of beachfront living and deliver solutions that protect your investment while enhancing curb appeal.',
      features: [
        'Coastal-grade materials',
        'Weather resistance',
        'Color consultation',
        'Minimal disruption',
        'Quality finishes',
        'Extended warranty'
      ],
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial & Hospitality Painting',
      description:
        'Professional painting for West Beach\'s restaurants, guesthouses, and businesses serving the beachfront community. We understand the importance of maintaining operations in this tourism-focused area and offer flexible scheduling to minimize disruption. Our efficient processes ensure quick turnaround while delivering results that enhance your business image.',
      features: [
        'After-hours service',
        'Tourism-ready finishes',
        'Brand color matching',
        'Quick completion',
        'Hospitality expertise',
        'Professional standards'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Coastal Painting',
      description:
        'Comprehensive exterior painting services specifically designed for West Beach\'s Atlantic beachfront environment. Our weather-resistant systems protect against salt spray, powerful winds, and intense sun exposure while maintaining beautiful aesthetics. We use premium products formulated for extreme coastal conditions to ensure maximum longevity.',
      features: [
        'Weather-resistant coatings',
        'Wind protection',
        'Salt air defense',
        'Sun protection',
        'Color retention',
        'Comprehensive warranty'
      ],
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Interior Beach House Painting',
      description:
        'Expert interior painting services for West Beach properties featuring beach-inspired finishes, custom color consultation, and meticulous attention to detail. We work with eco-friendly, low-odor materials perfect for coastal homes, creating relaxed, stylish interiors that complement the beachfront lifestyle and ocean views.',
      features: [
        'Beach-inspired finishes',
        'Color consultation',
        'Low-odor materials',
        'Furniture protection',
        'Detailed preparation',
        'Clean workmanship'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Waterproofing & Wind Damage Repair',
      description:
        'Comprehensive waterproofing solutions and repair services for West Beach properties experiencing coastal weather damage. Our expert approach addresses both aesthetic and structural concerns, including wind damage repair, crack restoration, and protective coatings to ensure your property remains beautiful and protected against Atlantic coastal elements.',
      features: [
        'Wind damage repair',
        'Waterproofing systems',
        'Crack restoration',
        'Storm protection',
        'Preventive maintenance',
        'Structural protection'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Beachfront Property Assessment',
      description:
        'Comprehensive evaluation of your West Beach property, including inspection of direct ocean exposure, wind impact, and existing paint condition. We assess salt spray damage, wind erosion, and UV degradation to develop a customized protection strategy tailored to Atlantic beachfront conditions and your property\'s specific location.',
    },
    {
      step: 2,
      title: 'Marine-Grade Material Selection',
      description:
        'Expert consultation on the best marine-grade paints and coatings for beachfront properties, considering extreme coastal conditions. We recommend premium products specifically formulated for direct ocean exposure, providing superior protection against salt spray, wind, and UV radiation while delivering beautiful, long-lasting results.',
    },
    {
      step: 3,
      title: 'Thorough Surface Preparation',
      description:
        'Meticulous preparation including salt deposit removal, wind damage repair, surface restoration, and premium primer application. Our detailed prep work ensures optimal adhesion and longevity of the coating system, addressing any underlying issues caused by coastal exposure before painting begins.',
    },
    {
      step: 4,
      title: 'Expert Application',
      description:
        'Professional application by experienced coastal painting specialists using advanced techniques and premium equipment. We employ multiple coat systems with proper curing times between applications to ensure maximum durability and wind resistance. Work is scheduled around weather conditions for optimal results.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Rigorous quality control inspection of all painted surfaces, checking for uniform coverage, proper adhesion, wind resistance, and excellent finish. We ensure every detail meets our high standards for beachfront properties and your expectations before project completion, with comprehensive documentation.',
    },
    {
      step: 6,
      title: 'Coastal Maintenance Guidance',
      description:
        'Detailed maintenance recommendations and care instructions specific to beachfront properties in Atlantic coastal environments. We provide guidance on salt removal, inspection schedules, and preventive maintenance to maximize the lifespan of your coating system in West Beach\'s demanding conditions.',
    },
  ];

  const recentProjects = [
    {
      title: 'Oceanfront Beach House Transformation',
      location: 'West Beach Beachfront',
      description:
        'Complete exterior and interior painting of a luxury 4-bedroom beach house with direct ocean access. Project included advanced marine-grade exterior coatings, wind-resistant systems, beach-inspired interior finishes, and comprehensive waterproofing. Spectacular Table Mountain and ocean views preserved throughout.',
      image:
        'https://public.readdy.ai/ai/img_res/a6b7e4f8605715f1b631bd3de1af203b.jpg',
    },
    {
      title: 'Beachfront Restaurant Renovation',
      location: 'West Beach Commercial Strip',
      description:
        'Comprehensive painting of a popular beachfront restaurant including dining areas, deck, and exterior facades. Project completed using after-hours scheduling to minimize business disruption. Included specialized coatings for high-traffic areas and extreme coastal exposure with ocean spray protection.',
      image:
        'https://readdy.ai/api/search-image?query=Beachfront%20restaurant%20painting%20in%20West%20Beach%20Blouberg%2C%20coastal%20dining%20establishment%20with%20ocean%20views%2C%20professional%20commercial%20painting%20Cape%20Town%2C%20modern%20beach%20restaurant%20with%20Table%20Mountain%20backdrop&width=400&height=300&seq=west-beach-project-2&orientation=landscape',
    },
    {
      title: 'Coastal Apartment Complex',
      location: 'West Beach Residential',
      description:
        'Exterior painting and waterproofing of a 12-unit beachfront apartment complex. Project included complete facade restoration, balcony waterproofing, wind damage repair, and marine-grade protective coatings. Coordinated with body corporate and residents for minimal disruption.',
      image:
        'https://readdy.ai/api/search-image?query=Coastal%20apartment%20complex%20painting%20in%20West%20Beach%20Blouberg%2C%20beachfront%20residential%20building%20with%20ocean%20views%2C%20professional%20building%20painting%20Cape%20Town%2C%20modern%20apartments%20with%20marine-grade%20coatings&width=400&height=300&seq=west-beach-project-3&orientation=landscape',
    },
    {
      title: 'Modern Beach Villa Makeover',
      location: 'West Beach Premium Properties',
      description:
        'Complete interior and exterior transformation of a contemporary beach villa. Included custom beach-inspired color scheme, premium finishes throughout, specialized coatings for coastal protection, and wind-resistant exterior systems. Project showcases stunning ocean and mountain views.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20beach%20villa%20painting%20in%20West%20Beach%20Blouberg%2C%20contemporary%20coastal%20property%20with%20panoramic%20views%2C%20professional%20painting%20services%2C%20stylish%20beachfront%20home%20design%20Cape%20Town&width=400&height=300&seq=west-beach-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Michael Stevens',
      location: 'West Beach Oceanfront Home',
      rating: 5,
      text:
        'Exceptional service for our beachfront property. The marine-grade coatings have withstood three years of direct ocean exposure, strong winds, and salt spray without any issues. The team understood the unique challenges of beachfront painting and delivered outstanding results. Highly recommend.',
    },
    {
      name: 'Lisa Botha',
      location: 'West Beach Restaurant',
      rating: 5,
      text:
        'Outstanding commercial painting service for our beachfront restaurant. They worked after hours so we didn\'t lose business, and the quality is exceptional. The coatings have held up beautifully despite constant ocean spray and wind. Our customers love the fresh, inviting appearance.',
    },
    {
      name: 'James Kruger',
      location: 'West Beach Apartment',
      rating: 5,
      text:
        'The painting of our beachfront apartment exceeded expectations. They handled the wind and weather challenges professionally, and the results are stunning. The ocean views are perfectly complemented by the beautiful finishes. The protection against coastal elements gives us complete peace of mind.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=West%20Beach%20Blouberg%20beachfront%20properties%20Cape%20Town%20being%20painted%2C%20oceanfront%20homes%20with%20Table%20Mountain%20views%2C%20professional%20painters%20working%20on%20coastal%20properties%2C%20stunning%20Atlantic%20beachfront%20with%20kitesurfing%20and%20mountain%20backdrop&width=1920&height=600&seq=west-beach-hero-main&orientation=landscape"
            alt="Professional painting services in West Beach, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">West Beach Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Specialized beachfront painting services for West Beach&apos;s coastal properties.
              Expert marine‑grade coatings and wind-resistant systems for homes and businesses in this spectacular Atlantic beachfront location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for West Beach&apos;s Beachfront Community
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                West Beach is one of Cape Town&apos;s most spectacular beachfront locations, renowned for its stunning Atlantic Ocean views, iconic Table Mountain backdrop, and vibrant beach lifestyle. This popular coastal area demands painting services that understand both the unique challenges of direct ocean exposure and the aesthetic expectations of beachfront property owners who value the area&apos;s natural beauty.
              </p>
              <p>
                Our specialized painting services for West Beach properties combine advanced marine-grade materials, expert coastal craftsmanship, and deep understanding of Atlantic beachfront environmental factors. The area&apos;s direct exposure to ocean spray, powerful south-easterly winds, intense UV radiation, and salt-laden air requires robust coating systems and specialized application techniques that go far beyond standard painting practices.
              </p>
              <p>
                Whether you own a beachfront home with ocean access, manage a restaurant or guesthouse serving the beach community, or have a property in the residential areas, our team delivers painting solutions that protect your investment while enhancing its appearance. We understand that West Beach properties represent significant investments in one of Cape Town&apos;s most desirable locations, and our services are designed to preserve and enhance their value through superior coastal protection.
              </p>
              <p>
                The unique character of West Beach, with its active beach lifestyle, water sports culture, and tourism focus, creates specific painting requirements. From protecting beachfront homes against direct ocean spray to maintaining the appearance of commercial properties serving beach visitors, our team has the expertise and experience to handle any coastal painting project with professionalism and attention to detail.
              </p>
              <p>
                West Beach&apos;s position on the Atlantic coast creates one of the most demanding environments for painted surfaces in Cape Town. The combination of direct ocean exposure, powerful winds that can reach gale force during summer months, constant salt spray, and intense UV reflection from both ocean and sand creates conditions that rapidly deteriorate inferior paint systems. Our comprehensive approach uses advanced marine-grade coatings specifically engineered for extreme coastal conditions, ensuring your property maintains its beauty and protection year after year despite these challenging environmental factors. We also understand the importance of timing work around weather conditions and the busy summer season to minimize disruption to the beach lifestyle that makes West Beach so special.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Beachfront Painting Services in West Beach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for West Beach&apos;s oceanfront properties, coastal homes, and beachfront businesses. Our services are tailored to withstand the demanding Atlantic coastal environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding West Beach&apos;s Extreme Atlantic Beachfront Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                West Beach&apos;s direct Atlantic Ocean exposure creates some of the most challenging conditions for painted surfaces in Cape Town. Properties face constant assault from salt spray carried by ocean winds, with beachfront homes experiencing direct ocean spray during high tides and storms. This relentless salt exposure causes rapid deterioration of standard paint systems, leading to premature failure, corrosion, and surface degradation.
              </p>
              <p>
                The powerful south-easterly winds that make West Beach famous for kitesurfing and windsurfing also create significant challenges for painted surfaces. These strong winds carry salt spray considerable distances inland, affecting not just beachfront properties but also homes several blocks from the ocean. Wind-driven sand can cause abrasion of painted surfaces, while the force of the wind itself can test the adhesion of coating systems. Our wind-resistant formulations are specifically designed to withstand these extreme conditions.
              </p>
              <p>
                UV radiation intensity in West Beach is exceptionally high due to the reflective properties of both the Atlantic Ocean and the white sand beach. This double reflection creates intense sun exposure that causes rapid fading and chalking of inferior paint products. The combination of UV exposure, salt spray, and wind creates a perfect storm of deterioration factors that require premium, marine-grade coatings specifically engineered for extreme coastal conditions.
              </p>
              <p>
                Moisture management is particularly critical for West Beach properties due to the combination of ocean humidity, wind-driven rain, direct ocean spray, and occasional storm surge during winter months. This moisture-rich environment can penetrate building envelopes if proper waterproofing and coating systems are not employed. Our comprehensive approach addresses these moisture challenges through advanced preparation techniques, specialized primers, and multi-layer protection systems designed specifically for Atlantic beachfront conditions, ensuring your property remains protected and beautiful despite the demanding environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Specialized Painting Process for West Beach Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results and maximum protection for beachfront properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why West Beach Properties Choose Our Beachfront Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Beachfront Expertise',
                    description:
                      'Specialized knowledge of Atlantic beachfront painting challenges and marine-grade solutions for West Beach properties. Our team has extensive experience working in extreme coastal conditions and understands the specific requirements for lasting protection against direct ocean exposure.',
                  },
                  {
                    title: 'Marine-Grade Protection',
                    description:
                      'Advanced coating systems specifically engineered for extreme coastal conditions including direct ocean spray, powerful winds, and intense UV exposure. We use premium products that deliver exceptional durability and protection in the most demanding beachfront environments.',
                  },
                  {
                    title: 'Wind-Resistant Systems',
                    description:
                      'Specialized application techniques and products designed to withstand West Beach\'s powerful south-easterly winds. Our wind-resistant formulations ensure superior adhesion and longevity even in the most challenging wind conditions.',
                  },
                  {
                    title: 'Weather-Aware Scheduling',
                    description:
                      'Expert timing of work around weather conditions and seasonal factors to ensure optimal results. We understand the importance of working during suitable weather windows and coordinate carefully to minimize disruption to the beach lifestyle.',
                  },
                  {
                    title: 'Local Beach Community Knowledge',
                    description:
                      'Deep understanding of West Beach\'s unique character, beach lifestyle, and community values. We respect the area\'s vibrant atmosphere and work to enhance properties while maintaining the relaxed, beach-focused lifestyle that makes West Beach special.',
                  },
                  {
                    title: 'Comprehensive Warranty',
                    description:
                      'Extensive warranty coverage on both materials and workmanship, providing complete peace of mind for your beachfront investment. Our guarantees reflect our confidence in the quality and durability of our work in extreme coastal conditions.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20West%20Beach%20Blouberg%20beachfront%20property%2C%20oceanfront%20home%20painting%20with%20Table%20Mountain%20views%2C%20quality%20marine-grade%20painting%20equipment%2C%20coastal%20property%20maintenance%20Cape%20Town&width=600&height=700&seq=west-beach-team-work&orientation=portrait"
                alt="Professional beachfront painting services in West Beach"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent West Beach Beachfront Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our beachfront painting projects in West Beach, demonstrating our commitment to
              quality and durability in this spectacular Atlantic coastal location
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What West Beach Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied beachfront property owners in West Beach
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Protect Your West Beach Beachfront Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a specialized beachfront consultation and detailed quote. Experience expert
            painting services designed specifically for West Beach&apos;s Atlantic coastal environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}